import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import 'intro.js/introjs.css';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';

// Components
import { RecommendationsTable } from '@/screens/optimization/components/recommendations-table';
import Top10Costs from '@/components/shared-charts/top-10-costs';
import OptimizationPotential from '@/screens/optimization/components/optimization-potential';
import OptimizationStatusCharts from '@/screens/optimization/components/optimization-status-charts';
import OptimizationFilters from '@/screens/optimization/components/optimization-filters';
import { ActionCenterV2 } from '@/components/action-center/action-center-v2';

// Utils
import { urls } from '@/urls';
import { getToken, getUser } from '@/lib/common';
import AppCardContainer from '@/components/app-card/app-card-container';
import { AppCard } from '@/components/app-card/app-card';
import { AppContainer, CHART_COLORS } from '@/components/ui';
import { useActionItemDetails } from '@/hooks/useActionItemDetails';
import ActionItemTextDialog from '@/components/action-center/action-item-text-dialog';
import { useActionCenterViewController } from '@/hooks/useActionCenterViewController';
import {
  createOptimizationActionCenterHandler,
  OPTIMIZATION_RECOMMENDATIONS_CARD_ID,
} from '@/screens/optimization/action-center-view.config';
import { useOptimizationFilters } from '@/screens/optimization/hooks/useOptimizationFilters';
import type { OptimizationFilterOptions } from '@/screens/optimization/types';
import { StatCard, StatRow } from '@/components/ds';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { formatCurrencyNoUnit } from '@/lib/formatters';

const EMPTY_FILTER_OPTIONS: OptimizationFilterOptions = {
  cloudAccounts: [],
  departments: [],
  subscriptions: [],
  systems: [],
  users: [],
};

// Top-of-screen summary stats, derived from the endpoints the screen already
// uses (optimizationPotential + recommendations). No new backend endpoints.
interface OptimizationSummaryStats {
  activeCount: number | null;
  monthlySavingPotential: number | null;
  implementedMonthly: number | null;
  efficientPct: number | null;
}

function Optimization() {
  const user = getUser();
  const {
    filters,
    setFilters,
    queryParams,
    setScopeFilter,
  } = useOptimizationFilters(user);
  const [filterOptions, setFilterOptions] =
    useState<OptimizationFilterOptions>(EMPTY_FILTER_OPTIONS);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
  const [filterOptionsError, setFilterOptionsError] = useState<string | null>(
    null,
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [actionCenterItems, setActionCenterItems] = useState<ActionCenterItemV2[]>([]);
  const [selectedActionItemId, setSelectedActionItemId] = useState<string | null>(null);
  const { item: v2Item } = useActionItemDetails('Optimization', selectedActionItemId, actionCenterItems);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [summaryStats, setSummaryStats] = useState<OptimizationSummaryStats>({
    activeCount: null,
    monthlySavingPotential: null,
    implementedMonthly: null,
    efficientPct: null,
  });

  // Derive the four top stat cards from the existing endpoints:
  // - optimizationPotential -> real_cost / active_optimization_potential / rejected_savings
  // - recommendations (all)  -> active count + implemented monthly savings
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [potentialRes, recs] = await Promise.all([
          fetchWrapper.get(urls.DevOps.optimizationPotential),
          fetchWrapper.get(`${urls.DevOps.recommendations}?statusFilter=all`),
        ]);
        if (cancelled) return;

        const potential = Array.isArray(potentialRes) ? potentialRes[0] : potentialRes;
        const realCost = Number(potential?.real_cost) || 0;
        const activePotential = Number(potential?.active_optimization_potential) || 0;
        const rejected = Number(potential?.rejected_savings) || 0;
        const denominator = realCost + activePotential + rejected;

        const list: any[] = Array.isArray(recs) ? recs : [];
        const activeCount = list.filter((r) => r?.status === 'Active').length;
        const implementedMonthly = list
          .filter((r) => r?.status === 'Implemented')
          .reduce((sum, r) => sum + (Number(r?.monthly_savings) || 0), 0);

        setSummaryStats({
          activeCount,
          monthlySavingPotential: activePotential,
          implementedMonthly,
          efficientPct: denominator > 0 ? Math.round((realCost / denominator) * 100) : 0,
        });
      } catch (error) {
        console.error('There was an error!', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useActionCenterViewController({
    module: 'Optimization',
    items: actionCenterItems,
    handler: createOptimizationActionCenterHandler({
      setSelectedItemId: setSelectedActionItemId,
      setIsDialogOpen,
    }),
  });

  useEffect(() => {
    setFilterOptionsLoading(true);
    setFilterOptionsError(null);
    Promise.all([
      fetchWrapper.get(urls.DevOps.getFilterCloudAccounts),
      fetchWrapper.get(urls.DevOps.getFilterDepartments),
      fetchWrapper.get(urls.DevOps.getFilterSubscriptions),
      fetchWrapper.get(urls.DevOps.getFilterSystems),
      fetchWrapper.get(urls.DevOps.getFilterUsers),
    ])
      .then(([cloudAccounts, departments, subscriptions, systems, users]) => {
        setFilterOptions({
          cloudAccounts,
          departments,
          subscriptions,
          systems,
          users,
        });
      })
      .catch((error) => {
        console.error('Error loading optimization filter options:', error);
        setFilterOptionsError('Failed to load filter options');
      })
      .finally(() => {
        setFilterOptionsLoading(false);
      });
  }, []);

  const handleRecommendationAction = () => {
    setRefreshKey((key) => key + 1);
  };

  if (!getToken()) {
    return <Navigate to="/" replace />;
  }

  return (
    <AppContainer>

      <StatRow columns={4}>
        <StatCard
          icon="spark"
          tone="primary"
          value={summaryStats.activeCount ?? '—'}
          label="Active recommendations"
        />
        <StatCard
          icon="dollar"
          tone="warn"
          iconColor="#F28E2C"
          iconTint="#FDEFDE"
          value={
            summaryStats.monthlySavingPotential != null
              ? formatCurrencyNoUnit(summaryStats.monthlySavingPotential)
              : '—'
          }
          label="Monthly saving potential"
        />
        <StatCard
          icon="shield"
          tone="success"
          value={
            summaryStats.implementedMonthly != null
              ? formatCurrencyNoUnit(summaryStats.implementedMonthly)
              : '—'
          }
          label="Implemented (mo)"
        />
        <StatCard
          icon="spark"
          tone="ai"
          value={summaryStats.efficientPct != null ? `${summaryStats.efficientPct}%` : '—'}
          label="Efficient spending"
        />
      </StatRow>

      <ActionCenterV2
        endpointUrl={urls.DevOps.getOptimizationActionCenterV2}
        scopeFilter={filters.scopeFilter}
        onScopeFilterChange={setScopeFilter}
        onItemsLoaded={setActionCenterItems}
      />

      <ActionItemTextDialog
        item={v2Item}
        currentUserId={user?.id}
        open={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedActionItemId(null);
        }}
      />

      <AppCardContainer>
        <AppCard
          className="card100"
          title="Recommendations"
          intro="Optimization Center show all recommendations identified by IncreDevo. We show you the recommendation name, Potenial monthly savings by implementing this recommendation, current total monthly cost of resources that this recommendation is applicable for, abd the person responsible for those resources from technical perspective."
          icon={<BarChartOutlinedIcon color="primary" />}
          attributes={{ id: OPTIMIZATION_RECOMMENDATIONS_CARD_ID }}>
          <OptimizationFilters
            filters={filters}
            onFiltersChange={setFilters}
            data={filterOptions}
            loading={filterOptionsLoading}
            error={filterOptionsError}
          />
          <RecommendationsTable
            filterQueryParams={queryParams}
            refreshKey={refreshKey}
            onRecommendationAction={handleRecommendationAction}
          />
        </AppCard>
      </AppCardContainer>

      <AppCardContainer>
        <AppCard
          className="card40"
          title="Optimization Potential"
          intro="Optimization Potential shows how much money in percentage you could save by applying all the recommendations."
          icon={<BarChartOutlinedIcon color="primary" />}>
          <OptimizationPotential />
        </AppCard>

        <AppCard
          className="card60"
          title="Top 10 Saving Potential by Recommendation Type"
          intro="Top 10 recommendations shows you recommendations which, if implemented, could save you the most."
          icon={<BarChartOutlinedIcon color="primary" />}>
          <Top10Costs url={urls.DevOps.top10recommendations} color={CHART_COLORS[1]} />
        </AppCard>
      </AppCardContainer>

      <AppCardContainer>
          <OptimizationStatusCharts display="all" />
      </AppCardContainer>
    </AppContainer>
  );
}

export default Optimization;
